import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

export default function TermsPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto px-6 md:px-12 py-12" data-testid="terms-page">
        <h1 className="text-3xl md:text-4xl font-heading font-semibold mb-2">Terms of Service</h1>
        <p className="text-sm text-muted-foreground mb-8">Last updated: February 13, 2026</p>

        <div className="prose prose-sm max-w-none space-y-6 text-foreground/90">
          <section>
            <h2 className="text-xl font-heading font-semibold mb-2">1. Acceptance of Terms</h2>
            <p>By accessing or using Christ Kingdom Global Marketplace ("the Platform", "we", "us"), operated by Datasamllc, you agree to be bound by these Terms of Service. If you do not agree, please do not use the Platform.</p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold mb-2">2. Eligibility</h2>
            <p>You must be at least 18 years old and legally able to enter into contracts to use our services. Vendors must operate a legitimate business and comply with all applicable laws.</p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold mb-2">3. Account Registration</h2>
            <p>You are responsible for maintaining the confidentiality of your account credentials. You agree to provide accurate, current, and complete information and to update it as necessary.</p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold mb-2">4. Buyers</h2>
            <p>As a buyer, you agree to pay for the items you order at the prices shown at checkout. Payment is processed securely via Stripe. Upon purchase, a contract is formed directly between you and the vendor.</p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold mb-2">5. Vendors (Sellers)</h2>
            <p>As a vendor, you agree to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Accurately describe your products and fulfill orders in a timely manner</li>
              <li>Respond to buyer inquiries within 2 business days</li>
              <li>Comply with our 10% marketplace commission on each completed sale</li>
              <li>Not list prohibited, counterfeit, or illegal items</li>
              <li>Maintain any licenses or permits required in your jurisdiction</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold mb-2">6. Commission & Payouts</h2>
            <p>The Platform charges a <strong>10% flat commission</strong> on every completed sale (or a <strong>7% reduced commission</strong> for qualifying Founding Vendors). Vendors receive payouts via their connected Stripe account. Oil & Gas commodity listings use market pricing with separate commission structures disclosed at listing time.</p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold mb-2">7. Taxes — Vendor Responsibility</h2>
            <p><strong>The Platform does not collect, calculate, file, or remit sales tax, use tax, value-added tax (VAT), goods and services tax (GST), or any other transaction-based taxes on behalf of Vendors, except where expressly required by law.</strong></p>
            <p className="mt-3">As a Vendor, you acknowledge and agree that you are solely responsible for:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Determining whether your sales create a tax-collection obligation ("nexus") in any jurisdiction;</li>
              <li>Obtaining and maintaining all required sales-tax permits, VAT/GST registrations, and business licenses in your operating jurisdictions;</li>
              <li>Calculating, collecting, reporting, and remitting all applicable transaction taxes to the appropriate tax authorities;</li>
              <li>Pricing your listings inclusive of any taxes you intend to recover, since the Platform displays a single buyer-facing price at checkout;</li>
              <li>Paying any income tax, self-employment tax, or business tax owed on your earnings from the Platform.</li>
            </ul>
            <p className="mt-3"><strong>Marketplace Facilitator Laws.</strong> In certain U.S. states and other jurisdictions, "marketplace facilitator" laws may require the Platform to collect and remit sales tax on behalf of Vendors. Where such laws apply, the Platform reserves the right (but not the obligation, unless required by law) to calculate, collect, and remit those taxes directly, and to deduct the corresponding amount from buyer payments before payout. Vendors will be notified in advance where this applies.</p>
            <p className="mt-3"><strong>1099-K and Tax Reporting (U.S. Vendors).</strong> Where required by U.S. federal or state law, Stripe will issue Form 1099-K to U.S. Vendors who exceed the applicable annual transaction thresholds. Vendors are responsible for keeping their tax identification information (EIN or SSN), business name, and address current in their Stripe Connect account. International Vendors may be required to provide equivalent tax documentation under their local laws.</p>
            <p className="mt-3"><strong>Indemnification.</strong> You agree to indemnify, defend, and hold harmless Datasamllc, its affiliates, officers, and employees from and against any and all claims, assessments, penalties, interest, fines, and reasonable legal fees arising from your failure to collect, report, or remit any taxes related to your sales on the Platform.</p>
            <p className="mt-3 text-sm text-muted-foreground"><em>This section does not constitute tax advice. Vendors are strongly encouraged to consult a qualified tax professional in their jurisdiction.</em></p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold mb-2">8. Prohibited Items</h2>
            <p>The following are strictly prohibited: weapons, drugs, counterfeit goods, stolen items, adult content, and any items that violate the ethical principles of a Christian marketplace. We reserve the right to remove listings at our discretion.</p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold mb-2">9. Refunds & Disputes</h2>
            <p>Refund eligibility is governed by our <a href="/refund-policy" className="text-primary underline">Refund Policy</a>. Disputes between buyers and vendors should first be attempted in good faith; the Platform may mediate but is not a party to the sale contract.</p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold mb-2">10. Intellectual Property</h2>
            <p>You retain ownership of content you upload (product photos, descriptions). By uploading, you grant us a non-exclusive, worldwide license to display that content on the Platform.</p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold mb-2">11. Limitation of Liability</h2>
            <p>The Platform is provided "as is". To the maximum extent permitted by law, Datasamllc and its affiliates are not liable for indirect, incidental, or consequential damages arising from your use of the Platform.</p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold mb-2">12. Termination</h2>
            <p>We may suspend or terminate accounts that violate these Terms. You may delete your account at any time by contacting support.</p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold mb-2">13. Governing Law</h2>
            <p>These Terms are governed by the laws of the United States of America and the State of New Jersey, without regard to conflict-of-law principles.</p>
          </section>

          <section>
            <h2 className="text-xl font-heading font-semibold mb-2">14. Contact</h2>
            <p>Questions? Contact us at <a href="mailto:support@christkingdomglobalmarketplace.com" className="text-primary underline">support@christkingdomglobalmarketplace.com</a>.</p>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
